"use client";

import { Header3, P2, P3 } from "@bumble/ui-kit";
import { LAST_UPDATED } from "./constants";
import styles from "./AiInterviewProcess.module.css";

/**
 * Page copy, deliberately hardcoded in English and served at every locale.
 *
 * It was agreed this page is not translated, so there is no DatoCMS record and
 * no lexemes behind it. The surrounding layout still renders <html lang> from
 * the route locale, so the article carries its own lang="en" — otherwise a
 * screen reader on /de would read English text with German pronunciation
 * rules.
 *
 * Source of truth is the Figma frame `Bumble-Web-Careers_AI-Process-Desktop`
 * (node 27904:81241). Keep LAST_UPDATED in step with any copy change.
 */

export default function Content() {
  return <article className={styles.copy} lang="en">
      <P3 isSubdued={true}>Last updated: {LAST_UPDATED}</P3>

      <P2>
        We’re an AI-forward company. We use AI every day, and we expect many
        candidates do too. That’s why we want to be clear about where AI is
        encouraged in our hiring process, and where we ask you to rely on your
        own thinking.
      </P2>

      <P2>
        Our goal is simple: to get to know you. We want to understand your
        experience, your judgment, and how you approach problems when there
        isn’t an obvious answer.
      </P2>

      <P2>
        Use AI to prepare, research, practice, and refine your ideas. Unless we
        specifically tell you that AI is part of the assessment, please refrain
        from using it to generate or suggest real-time responses. We want to
        understand how you think, reason, and solve problems, so we’d much
        rather hear your authentic thoughts during interviews and assessments.
        If a stage of the process is designed to assess how you work with AI,
        we’ll let you know upfront.
      </P2>

      <Header3 as="h2">Before your interview</Header3>

      <P2>
        Use AI to research us, build a study plan, generate practice questions,
        or help polish your written application. However, please be prepared to
        discuss any project or achievement mentioned in your application. During
        your interview, we’ll want to explore what you actually did, what you
        learned, and what you contributed.
      </P2>

      <P2>
        <strong>
          For example, if we’re asking about a time you solved a problem
          creatively:
        </strong>
      </P2>

      <P2>
        <strong>Great use of AI:</strong> Think of a real example from your
        experience, write down the key points, then ask AI to help you improve
        the structure, clarity, or wording.
      </P2>

      <P2>
        <strong>Not what we’re looking for:</strong> Asking AI to invent an
        example or substantially rewrite your experience into something that
        didn’t happen or doesn’t reflect your role. We’ll naturally ask
        follow-up questions because we’re interested in your thinking and
        experience, and the strongest conversations come from real examples.
      </P2>

      <Header3 as="h2">During live interviews</Header3>

      <P2>
        This is the part we actually look forward to: a real conversation. We
        want to understand how you think, work through problems, explain your
        reasoning, and respond in the moment.
      </P2>

      <P2>
        So during interviews and any live exercises, please don’t lean on AI
        tools to generate, suggest, or refine your responses in real time. It’s
        absolutely fine not to have every answer. Saying, “I’m not sure, but
        here’s how I’d approach it,” often tells us far more than a polished
        response ever could.
      </P2>

      <P2>
        One exception: some software engineering interviews include an AI
        collaboration exercise, where working with AI is part of what we’re
        testing. If that is part of your interview process, you’ll know going
        in.
      </P2>

      <Header3 as="h2">Take-home assessments: follow the brief</Header3>

      <P2>
        Every take-home assessment explains what we’re looking to learn and how
        AI fits into that exercise. Sometimes, we’ll encourage you to use AI.
        Other times, we’ll ask you to work independently. Either way, the brief
        will set our expectations.
      </P2>

      <P2>
        Always follow the instructions for each stage of the process. If AI use
        is permitted or forms part of the assessment, please be transparent
        about how you used it. We’re interested in understanding your approach,
        reasoning, and how you worked with the tool, not just the final output.
      </P2>

      <Header3 as="h2">Using AI as part of an accessibility adjustment</Header3>

      <P2>
        If you use AI tools as part of an accessibility adjustment—live
        captioning, transcription, note-taking—please let your recruiter know
        before your interview. We want to work to create a setup that works best
        for you.
      </P2>

      <Header3 as="h2">One friendly heads-up</Header3>

      <P2>
        Please avoid pasting any confidential information — yours or a current
        employer’s — into AI tools. It’s hard to know where that data goes once
        you’ve shared it.
      </P2>

      <P2>
        Questions about our approach to AI in the hiring process? Speak to your
        recruiter before you start. They’ll be happy to explain what to expect
        and answer any questions.
      </P2>
    </article>;
}